import React, { useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue.tsx';
import {
  type AssetDetailLabels,
  assetLocation, assetLocationColumns,
  assetSpec, assetSpecsColumns, customerAttributes,
  leaseDetails, leaseDetailsColumns,
  nonLeasedAssetSpec, nonLeasedAssetSpecsColumns,
  nonLeasedAssetLocation, nonLeasedAssetLocationColumns,
  nonLeasedLeaseDetails, nonLeasedLeaseDetailsColumns,
  nonLeasedCustomerAttributes} from './assetDetailLabels.ts';
import PrintIcon from '@mui/icons-material/Print';
import RoundedButton from '../../RoundedButton/RoundedButton.tsx';
import HuntingtonSymbolGreen from '../../../assets/Huntington_Symbol_RGB_Green.png';
import Typography from "@mui/material/Typography";
import { useSelector } from 'react-redux';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import {useGetAssetUserDefinedFieldsQuery} from "../../../api/asset.api.ts";
import type {UserDefinedFieldHeadings} from "../../../api/models/asset.model.ts";
import { selectIsPrivateLabel } from '../../../redux/slices/uiSettingsSlice.ts';
import { FormatDate } from '../../../utils/helpers.ts';

interface AssetDetailModalProps {
  open: boolean;
  onClose: () => void;
  asset: any | null;
  onAuditLogClick: () => void;
  isNonLeasedAsset?: boolean;
}

const buildCustomerDefinedDetailColumns = (userDefinedFieldDefinitions: UserDefinedFieldHeadings) : AssetDetailLabels[] => {
    const userDefinedColumns: AssetDetailLabels[] = [];
    Object.keys(userDefinedFieldDefinitions).forEach((key) => {
        const userDefinedValue = userDefinedFieldDefinitions[key as keyof UserDefinedFieldHeadings]
        if (userDefinedValue) {
            userDefinedColumns.push({
                field: key,
                headerName: userDefinedValue
            });
        }
    });
    return userDefinedColumns;
}


const AssetDetailModal: React.FC<AssetDetailModalProps> = ({
  open,
  onClose,
  asset,
  onAuditLogClick,
  isNonLeasedAsset = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [huntingtonOwned] = useState(true);
  const isPrivateLabel = useSelector(selectIsPrivateLabel);

  const account = useSelector(selectAccount);
  const accountId = account?.accountId ?? 0;
  const { data: userDefinedFieldDefinitions } = useGetAssetUserDefinedFieldsQuery({ accountId });

  if (!asset) return null;

  let headersAndColumns: Record<string, AssetDetailLabels[]>;
  if (isNonLeasedAsset) {
    headersAndColumns = {
      [nonLeasedAssetSpec]: nonLeasedAssetSpecsColumns,
      [nonLeasedCustomerAttributes]: userDefinedFieldDefinitions ? buildCustomerDefinedDetailColumns(userDefinedFieldDefinitions) : [],
      [nonLeasedAssetLocation]: nonLeasedAssetLocationColumns,
      [nonLeasedLeaseDetails]: nonLeasedLeaseDetailsColumns
    };
  } else {
    headersAndColumns = {
      [assetSpec]: assetSpecsColumns,
      [customerAttributes]: userDefinedFieldDefinitions ? buildCustomerDefinedDetailColumns(userDefinedFieldDefinitions) : [],
      [assetLocation]: assetLocationColumns,
      [leaseDetails]: leaseDetailsColumns
    };
  }

  const headers = Object.keys(headersAndColumns);

  return (
    <ResponsiveDialogue
      data-testid="asset-detail-modal"
      open={open}
      onClose={onClose}
      title={asset.assetNumber}
    >
      <Box data-testid="asset-detail-modal-content" display="flex" flexDirection="column" gap={2} paddingTop={2}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'flex-start'}
          gap={isMobile ? 1 : 2}
        >
          {!isMobile && <ActionButtons />}
        </Box>
        {headers.map(header => (
          <AssetDetailsGrid
            key={header}
            header={header}
            asset={asset}
            columns={headersAndColumns[header]}
            isMobile={isMobile}
          />
        ))}
      </Box>
      <AssetDetailFooter
        huntingtonOwned={huntingtonOwned}
        isMobile={isMobile}
        onAuditLogClick={onAuditLogClick}
        isPrivateLabel={isPrivateLabel}
      />
    </ResponsiveDialogue>
  );
};

const ActionButtons: React.FC = () => (
  <Box flex={1} display="flex" alignItems="center" justifyContent="flex-end" gap={2} mt={0}>
    <Box
      display="flex"
      alignItems="center"
      sx={{ cursor: 'pointer' }}
      onClick={() => window.print()}
    >
      <PrintIcon sx={{ mr: 0.5 }} />
      <span>Print</span>
    </Box>
    <Box
      display="flex"
      alignItems="center"
      sx={{ cursor: 'pointer' }}
      onClick={() => {}}
    >
      {/* TODO: Re-enable export functionality */}
      {/* <ImportExportOutlined sx={{ mr: 0.5 }} />
      <span>Export</span> */}
    </Box>
  </Box>
);

const AssetDetailsGrid: React.FC<{
    header: string;
  asset: any;
  columns: AssetDetailLabels[];
  isMobile?: boolean;
}> = ({ header, asset, columns, isMobile }) => {
    if (columns.length === 0) return null;

    if (isMobile) {
      // Stack boxes vertically on mobile
      return (
        <>
          <Typography
            variant="h5"
            sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
          >
            {header}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            {columns.map((field, index) => {
              const value = field.date
                ? FormatDate(asset[field.field])
                : asset[field.field] ?? '-';
              if (field.currency && (asset[field.field] === null || asset[field.field] === undefined)) {
                return null;
              }
              return (
                <Box key={index} display="flex" flexDirection="column" mb={1}>
                  <strong style={{ fontSize: '1rem' }}>{field.headerName}:</strong>
                  <Box mt={0.5} fontSize="0.95rem">
                    <>
                      <span>{field.currency ? "$" : ""}</span>
                      <span>{value}</span>
                    </>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </>
      );
    }

    // Desktop: use grid layout
    return (
      <>
        <Typography
          variant="h5"
          sx={{ color: (theme) => theme.palette.primary.main, fontWeight: 'bold' }}
        >
          {header}
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mt={2}>
          {columns.map((field, index) => {
            const value = field.date
              ? FormatDate(asset[field.field])
              : asset[field.field] ?? '-';
            if (field.currency && (asset[field.field] === null || asset[field.field] === undefined)) {
              return null;
            }
            return (
              <Box key={index} display="flex" flexDirection="column">
                <strong style={{ fontSize: '1.1rem' }} data-testid={field.field}>{field.headerName}:</strong>
                <Box mt={0.5} fontSize="1rem" data-testid={`${field.field}-value`}>
                  <>
                    <span>{field.currency ? "$" : ""}</span>
                    <span>{value}</span>
                  </>
                </Box>
              </Box>
            )
          })}
        </Box>
      </>
    );
  }


const AssetDetailFooter: React.FC<{
  huntingtonOwned: boolean;
  isMobile?: boolean;
  isPrivateLabel?: boolean;
  onAuditLogClick: () => void;
}> = ({ huntingtonOwned, isMobile, isPrivateLabel, onAuditLogClick }) => (
  <>
    {(huntingtonOwned && !isPrivateLabel) && (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        mt={isMobile ? 2 : 4}
        mb={isMobile ? 0.5 : 1}
      >
        <img
          src={HuntingtonSymbolGreen}
          alt="Huntington Symbol"
          data-testid="huntington-symbol"
          style={{ height: isMobile ? 22 : 28, marginRight: 8 }}
        />
        <Box component="span" sx={{ color: '#008A43', fontWeight: 600 }}>
          Huntington Owned
        </Box>
      </Box>
    )}
    <Box display="flex" justifyContent="flex-end" mt={isMobile ? 1.5 : 3}>
      <RoundedButton
        onClick={onAuditLogClick}
        data-testid="audit-log-button"
        sx={{ fontSize: isMobile ? '0.95rem' : '1rem', px: isMobile ? 2 : 3 }}
        variantStyle="dark"
        variant="contained"
      >
        Audit Log
      </RoundedButton>
    </Box>
  </>
);

export default AssetDetailModal;
